import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Tiny IntersectionObserver hook for scroll-triggered fade-in.
 * Adds .in-view to the .reveal element when it enters the viewport.
 */
export function useReveal() {
    useEffect(() => {
        if (typeof window === "undefined") return;
        const els = document.querySelectorAll(".reveal");
        if (!("IntersectionObserver" in window) || els.length === 0) {
            els.forEach((el) => el.classList.add("in-view"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
}

/** Tracks scroll past a px threshold (for sticky CTA). */
export function useScrolledPast(threshold = 600) {
    const [past, setPast] = useState(false);
    useEffect(() => {
        const onScroll = () => setPast(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return past;
}

/** Smooth-scroll to anchor with sticky-nav offset. */
export function useAnchorScroll(offset = 72) {
    return useCallback(
        (id) => {
            const el = document.getElementById(id);
            if (!el) return;
            const top =
                el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: "smooth" });
        },
        [offset]
    );
}

/** Focus-trap helper for modals. */
export function useFocusTrap(containerRef, isOpen) {
    useEffect(() => {
        if (!isOpen || !containerRef.current) return;
        const node = containerRef.current;
        const selector =
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
        const focusables = () =>
            Array.from(node.querySelectorAll(selector)).filter(
                (el) => !el.hasAttribute("disabled")
            );

        const first = focusables()[0];
        if (first) first.focus();

        const onKey = (e) => {
            if (e.key === "Tab") {
                const list = focusables();
                if (list.length === 0) return;
                const idx = list.indexOf(document.activeElement);
                if (e.shiftKey && idx <= 0) {
                    e.preventDefault();
                    list[list.length - 1].focus();
                } else if (!e.shiftKey && idx === list.length - 1) {
                    e.preventDefault();
                    list[0].focus();
                }
            }
        };
        node.addEventListener("keydown", onKey);
        return () => node.removeEventListener("keydown", onKey);
    }, [containerRef, isOpen]);
}

/** Used by HeroMockup to subtly tick numbers up to their final value. */
export function useCountUp(target, duration = 1400) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        // StrictMode-safe: each effect invocation starts and cleans up its own
        // animation. The final invocation that survives drives the value to target.
        let raf = 0;
        const start = performance.now();
        const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(eased * target));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return val;
}
